/**
 * Central Color System for All Plots
 * 
 * This file defines consistent colors for categories across all plots (Ragone, Score, My, Free, etc.)
 * Colors are designed to be:
 * - Visually distinct
 * - Accessible (good contrast)
 * - Scalable (supports new categories via fallback colors)
 * - Consistent across the entire application
 * 
 * Multiple palettes available for user preference
 */

import { signal } from '@preact/signals'

// ============================================
// PALETTE SELECTION STATE
// ============================================
export const selectedPalette = signal('corporate') // 'corporate' | 'vibrant' | 'colorblind' | 'pastel'

// ============================================
// COLOR PALETTES
// ============================================

/**
 * CORPORATE PALETTE
 * Batemo Orange/Cyan + complementary colors
 * Optimized for dark UI (#0d0d0d, #1a1a1a)
 * First 10 colors: Maximum contrast for high distinguishability
 * Remaining 20 colors: Extended palette for larger datasets
 */
export const CORPORATE_PALETTE = [
  // === FIRST 10: MAXIMUM DISTINCTION ===
  '#ea5b0c', // 0  - Batemo Orange (warm)
  '#0d7c99', // 1  - Batemo Cyan (cool)
  '#ef4444', // 2  - Red (bright warm)
  '#10b981', // 3  - Emerald (green)
  '#3b82f6', // 4  - Blue (bright cool)
  '#eab308', // 5  - Yellow (bright)
  '#8b5cf6', // 6  - Violet (purple)
  '#ec4899', // 7  - Pink (hot pink)
  '#06b6d4', // 8  - Cyan (light)
  '#22c55e', // 9  - Green (lime)
  
  // === NEXT 20: EXTENDED PALETTE ===
  '#f59e0b', // 10 - Amber (gold)
  '#14b8a6', // 11 - Teal
  '#a855f7', // 12 - Purple (bright)
  '#fb923c', // 13 - Orange (light)
  '#38bdf8', // 14 - Sky
  '#4ade80', // 15 - Green (light)
  '#f87171', // 16 - Red (coral)
  '#c084fc', // 17 - Purple (lavender)
  '#fbbf24', // 18 - Amber (light)
  '#f472b6', // 19 - Pink (rose)
  '#2dd4bf', // 20 - Teal (turquoise)
  '#a3e635', // 21 - Lime (bright)
  '#818cf8', // 22 - Indigo
  '#fb7185', // 23 - Rose (salmon)
  '#34d399', // 24 - Emerald (mint)
  '#fde047', // 25 - Yellow (light)
  '#67e8f9', // 26 - Cyan (aqua)
  '#d946ef', // 27 - Fuchsia (magenta)
  '#94a3b8', // 28 - Slate (cool gray)
  '#cbd5e1'  // 29 - Slate (light gray)
]

/**
 * VIBRANT PALETTE
 * Maximum saturation and contrast
 * Bold, highly distinguishable colors
 */
export const VIBRANT_PALETTE = [
  '#ff6b00', '#00d4ff', '#0066ff', '#00ff88', '#ffcc00',
  '#ff00ff', '#ff0000', '#00ffff', '#ff0088', '#00ff00',
  '#ff8800', '#8800ff', '#88ff00', '#ffff00', '#ff4400',
  '#0088ff', '#44ff00', '#ffaa00', '#cc00ff', '#ff0044',
  '#00ffaa', '#ff8800', '#4400ff', '#00ff44', '#aa00ff',
  '#ff0088', '#0044ff', '#ff4488', '#888888', '#cccccc'
]

/**
 * COLORBLIND SAFE PALETTE
 * Optimized for deuteranopia and protanopia
 * Uses blue/orange contrast, avoids red/green confusion
 */
export const COLORBLIND_PALETTE = [
  '#ea5b0c', '#0173b2', '#029e73', '#ece133', '#cc78bc',
  '#ca9161', '#949494', '#56b4e9', '#f0e442', '#009e73',
  '#e69f00', '#0072b2', '#d55e00', '#cc79a7', '#999999',
  '#ffb000', '#0090d0', '#00a878', '#fff033', '#dd88cc',
  '#ddaa77', '#aaaaaa', '#77ccff', '#fff888', '#33cc99',
  '#ffcc33', '#3399dd', '#ff7733', '#ee99bb', '#bbbbbb'
]

/**
 * PASTEL PALETTE
 * Soft, muted colors for reduced eye strain
 * Lower saturation, gentle on the eyes
 */
export const PASTEL_PALETTE = [
  '#ffb380', '#80d4e6', '#99b3ff', '#99e6b3', '#ffd999',
  '#d9b3ff', '#ff9999', '#99e6e6', '#ff99cc', '#99ffb3',
  '#ffc299', '#cc99ff', '#ccff99', '#ffff99', '#ffaa80',
  '#99ccff', '#b3ff99', '#ffe699', '#e699ff', '#ff99b3',
  '#99ffe6', '#ffc299', '#b380ff', '#99ffb3', '#d980ff',
  '#ff99cc', '#8099ff', '#ffb3cc', '#cccccc', '#e6e6e6'
]

/**
 * Get the active color palette based on user selection
 */
function getActivePalette() {
  switch (selectedPalette.value) {
    case 'vibrant':
      return VIBRANT_PALETTE
    case 'colorblind':
      return COLORBLIND_PALETTE
    case 'pastel':
      return PASTEL_PALETTE
    case 'corporate':
    default:
      return CORPORATE_PALETTE
  }
}

// Backward compatibility
export const UNIVERSAL_COLOR_PALETTE = CORPORATE_PALETTE

// ============================================
// YEAR COLOR GRADIENTS (per palette)
// ============================================
const YEAR_GRADIENTS = {
  corporate: {
    colorStart: '#0d7c99',  // Batemo Cyan (cool)
    colorEnd: '#ea5b0c'     // Batemo Orange (warm)
  },
  vibrant: {
    colorStart: '#00d4ff',  // Bright cyan
    colorEnd: '#ff6b00'     // Bright orange
  },
  colorblind: {
    colorStart: '#0173b2',  // Blue (safe)
    colorEnd: '#e69f00'     // Orange (safe)
  },
  pastel: {
    colorStart: '#80d4e6',  // Soft cyan
    colorEnd: '#ffb380'     // Soft orange
  }
}

/**
 * Get year gradient colors based on selected palette
 */
function getYearGradient() {
  return YEAR_GRADIENTS[selectedPalette.value] || YEAR_GRADIENTS.corporate
}

/**
 * Get color for a year value (interpolates between old and new)
 * Uses palette-specific gradient
 */
export function getYearColor(year) {
  const gradient = getYearGradient()
  if (!year) return gradient.colorStart
  
  const min = 2019
  const max = 2025
  const clampedYear = Math.max(min, Math.min(max, year))
  const ratio = (clampedYear - min) / (max - min)
  
  // Linear interpolation between start and end colors
  const start = {
    r: parseInt(gradient.colorStart.slice(1, 3), 16),
    g: parseInt(gradient.colorStart.slice(3, 5), 16),
    b: parseInt(gradient.colorStart.slice(5, 7), 16)
  }
  const end = {
    r: parseInt(gradient.colorEnd.slice(1, 3), 16),
    g: parseInt(gradient.colorEnd.slice(3, 5), 16),
    b: parseInt(gradient.colorEnd.slice(5, 7), 16)
  }
  
  const r = Math.round(start.r + (end.r - start.r) * ratio)
  const g = Math.round(start.g + (end.g - start.g) * ratio)
  const b = Math.round(start.b + (end.b - start.b) * ratio)
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Backward compatibility
export const YEAR_COLOR_SCALE = {
  min: 2019,
  max: 2025,
  colorStart: '#0d7c99',
  colorEnd: '#ea5b0c'
}

// ============================================
// MANUFACTURER COLORS (Scalable via Hashing)
// High contrast, visually distinct palette for maximum differentiation
// ============================================
export const MANUFACTURER_COLOR_PALETTE = [
  '#ea5b0c', // Batemo Orange - Brand primary
  '#3b82f6', // Blue 500 - Strong blue
  '#10b981', // Emerald 500 - Green
  '#ef4444', // Red 500 - Bright red
  '#a855f7', // Purple 500 - Distinct purple
  '#06b6d4', // Cyan 500 - Bright cyan
  '#f59e0b', // Amber 500 - Gold/amber
  '#ec4899', // Pink 500 - Hot pink
  '#14b8a6', // Teal 500 - Teal green
  '#f97316', // Orange 500 - True orange
  '#8b5cf6', // Violet 500 - Blue-purple
  '#0ea5e9', // Sky 500 - Sky blue
  '#84cc16', // Lime 500 - Yellow-green
  '#e11d48', // Rose 600 - Deep rose
  '#22c55e', // Green 500 - Pure green
  '#d946ef', // Fuchsia 500 - Bright fuchsia
  '#eab308', // Yellow 500 - Golden yellow
  '#f43f5e', // Rose 500 - Pink-red
  '#7c3aed', // Violet 600 - Deep violet
  '#06b6d4'  // Cyan 500 - Repeat for >20
]

/**
 * Category-to-index mapping cache
 * Stores sorted category lists per colorBy dimension
 * Format: { 'chemistry': ['LFP', 'NCA', 'NMC', ...], 'format': ['Cylindrical', 'Pouch', ...] }
 */
const categoryIndexCache = {}

/**
 * Get color index for a category based on its sorted position
 * First occurrence gets color 0, second gets color 1, etc.
 * This ensures maximum color separation and predictable assignment
 * 
 * @param {string} colorBy - The dimension (chemistry, format, etc.)
 * @param {string} category - The specific category value
 * @returns {number} Index in palette
 */
function getCategoryIndex(colorBy, category) {
  if (!category) return 0
  
  // Initialize cache for this dimension if not exists
  if (!categoryIndexCache[colorBy]) {
    categoryIndexCache[colorBy] = []
  }
  
  const cache = categoryIndexCache[colorBy]
  
  // Check if category already has an index
  let index = cache.indexOf(category)
  
  if (index === -1) {
    // New category - add to end and assign next index
    cache.push(category)
    index = cache.length - 1
  }
  
  return index
}

/**
 * Universal color getter for any category
 * Uses sequential palette colors based on sorted category order
 * First unique category gets color 0, second gets color 1, etc.
 * 
 * @param {string} value - Category value
 * @param {string} colorBy - The dimension (for caching)
 * @returns {string} Hex color
 */
export function getDynamicColor(value, colorBy = 'default') {
  if (!value) return '#64748b'
  const palette = getActivePalette()
  const index = getCategoryIndex(colorBy, value)
  return palette[index % palette.length]
}

/**
 * Get color for manufacturer (backward compatibility)
 */
export function getManufacturerColor(manufacturer) {
  return getDynamicColor(manufacturer, 'manufacturer')
}

/**
 * Get color for chemistry (dynamic)
 */
export function getChemistryColor(chemistry) {
  return getDynamicColor(chemistry, 'chemistry')
}

/**
 * Get color for country (dynamic)
 */
export function getCountryColor(country) {
  return getDynamicColor(country, 'country')
}

/**
 * Get color for format (dynamic)
 */
export function getFormatColor(format) {
  return getDynamicColor(format, 'format')
}

/**
 * Get color for housing/shape (fixed colors for cylindrical, prismatic, pouch)
 */
const HOUSING_COLORS = {
  'cylindrical': '#ea5b0c', // Batemo Orange
  'prismatic': '#0d7c99',   // Batemo Cyan
  'pouch': '#10b981',       // Emerald
}

export function getHousingColor(shape) {
  if (!shape) return '#64748b'
  const lower = shape.toLowerCase().trim()
  return HOUSING_COLORS[lower] || getDynamicColor(shape, 'housing')
}

/**
 * Get color for technology (dynamic)
 */
export function getTechnologyColor(technology) {
  return getDynamicColor(technology, 'technology')
}

// ============================================
// UNIFIED COLOR GETTER
// ============================================
/**
 * Get color for any category type
 * This is the main function to use across all plots
 * 
 * @param {string} colorBy - The color dimension ('chemistry', 'country', 'format', etc.)
 * @param {string|number} category - The specific category value
 * @returns {string} Hex color code
 */
export function getCategoryColor(colorBy, category) {
  if (!category) return '#64748b' // Default gray for undefined
  
  switch (colorBy) {
    case 'cathode-chemistry':
    case 'anode-chemistry':
    case 'chemistry':
      return getChemistryColor(category)
    
    case 'country':
      return getCountryColor(category)
    
    case 'format':
      return getFormatColor(category)
    
    case 'housing':
      return getHousingColor(category)
    
    case 'technology':
      return getTechnologyColor(category)
    
    case 'manufacturer':
      return getManufacturerColor(category)
    
    case 'year':
      return getYearColor(category)
    
    default:
      return getDynamicColor(category, colorBy) // Fallback with dimension
  }
}

// ============================================
// LEGEND DATA GENERATORS
// ============================================
/**
 * Get legend categories and colors for a specific colorBy dimension
 * This ensures legend matches actual plot colors
 * 
 * @param {string} colorBy - The color dimension
 * @param {Array} cells - The cell data to extract unique categories from
 * @returns {Array} Array of {category, color} objects
 */
export function getLegendData(colorBy, cells) {
  if (!cells || cells.length === 0) return []
  
  // Extract unique categories from cells
  const uniqueCategories = new Set()
  
  cells.forEach(cell => {
    let categoryValue
    
    switch (colorBy) {
      case 'cathode-chemistry': {
        // Cathode chemistry (main_chemistry, but LTO goes to anode)
        const mainChem = cell.chemical?.main_chemistry || cell.chemical?.cathode
        if (mainChem) {
          const upper = mainChem.toUpperCase()
          // LTO is an anode material, not cathode
          if (!(upper === 'LTO' || upper.includes('TITANATE') || upper.includes('LI4TI5O12'))) {
            categoryValue = mainChem
          }
        }
        break
      }
      case 'anode-chemistry': {
        // Anode chemistry (anode_material OR LTO if it's the main_chemistry)
        const mainChem = cell.chemical?.main_chemistry || cell.chemical?.cathode
        const anodeMat = cell.chemical?.anode
        
        // Check if main_chemistry is actually an anode material (e.g., LTO)
        if (mainChem) {
          const upper = mainChem.toUpperCase()
          if (upper === 'LTO' || upper.includes('TITANATE') || upper.includes('LI4TI5O12')) {
            categoryValue = mainChem
          }
        }
        // If not, use anode_material
        if (!categoryValue && anodeMat) {
          categoryValue = anodeMat
        }
        break
      }
      case 'chemistry':
        categoryValue = cell.chemical?.main_chemistry || cell.chemical?.cathode
        break
      case 'country':
        categoryValue = cell.general?.origin_country
        break
      case 'format':
        categoryValue = cell.mechanical?.format_group || cell.mechanical?.format
        break
      case 'housing':
        categoryValue = cell.mechanical?.shape
        break
      case 'technology':
        categoryValue = cell.general?.technology
        break
      case 'manufacturer':
        categoryValue = cell.general?.manufacturer
        break
      case 'year':
        if (cell.general?.date) {
          const year = parseInt(cell.general.date.split('-')[0])
          if (!isNaN(year)) categoryValue = year
        }
        break
    }
    
    if (categoryValue) uniqueCategories.add(categoryValue)
  })
  
  // Convert to sorted array with colors
  const legendData = Array.from(uniqueCategories).map(category => ({
    category: category,
    color: getCategoryColor(colorBy, category)
  }))
  
  // Sort by category name (except year which should be numeric)
  if (colorBy === 'year') {
    legendData.sort((a, b) => a.category - b.category)
  } else {
    legendData.sort((a, b) => String(a.category).localeCompare(String(b.category)))
  }
  
  return legendData
}

// ============================================
// EXPORTS
// ============================================
export default {
  // Palette selection
  selectedPalette,
  
  // Color palettes
  CORPORATE_PALETTE,
  VIBRANT_PALETTE,
  COLORBLIND_PALETTE,
  PASTEL_PALETTE,
  UNIVERSAL_COLOR_PALETTE,
  MANUFACTURER_COLOR_PALETTE,
  YEAR_COLOR_SCALE,
  
  // Functions
  getCategoryColor,
  getDynamicColor,
  getChemistryColor,
  getCountryColor,
  getManufacturerColor,
  getFormatColor,
  getHousingColor,
  getTechnologyColor,
  getYearColor,
  getLegendData
}
